import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, label } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useMatch, playersOf } from "../../lib/useMatch";
import { Pitch, FORMATIONS, formationSlots, layoutPositions } from "../../components/Pitch";
import { MatchHeader, TacticalHeader } from "../../components/MatchHeader";
import { EventTimeline } from "../../components/EventTimeline";
import { PageHeader, Panel, Spinner, Empty, Avatar, StatusBadge } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const PRE = ["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff", "delayed"];

export function useCoachTeams() {
  const { me } = useAuth();
  const ids = me.memberships.filter((m) => m.role === "coach" && m.status === "active").flatMap((m) => m.team_ids || []);
  const [teams, setTeams] = useState([]);
  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data.filter((t) => ids.includes(t.id)))); }, [ids.join()]); // eslint-disable-line
  return teams;
}

function SubRequests({ m, teamId, onField, bench }) {
  const [out, setOut] = useState("");
  const [inn, setInn] = useState("");
  const [list, setList] = useState([]);
  const load = () => api.get(`/matches/${m.id}/substitution-requests`).then((r) => setList(r.data.filter((x) => x.team_id === teamId)));
  useEffect(() => { load(); }, [m]); // eslint-disable-line
  const P = (id) => m.players[id];
  const Pick = ({ list, val, set, kind }) => (
    <div className="flex gap-2 overflow-x-auto pb-1">{list.map((p) => (
      <button key={p.id} data-testid={`sub-${kind}-${p.number}`} onClick={() => set(val === p.id ? "" : p.id)}
        className={`flex w-16 shrink-0 flex-col items-center gap-1 rounded-[14px] border p-1.5 text-[10px] font-bold ${val === p.id ? (kind === "out" ? "border-[#F02030] bg-[#F02030]/10" : "border-[#35865C] bg-[#35865C]/10") : "border-[#E7EAEE]"}`}>
        <Avatar player={p} size={40} /><span className="w-full truncate text-center">#{p.number} {p.short_name}</span>
      </button>
    ))}</div>
  );
  return (
    <Panel title="Substitution request" testid="sub-request-panel">
      <div className="tro-label mb-1 text-[#F02030]">Outgoing player</div>
      <Pick list={onField} val={out} set={setOut} kind="out" />
      <div className="tro-label mb-1 mt-2 text-[#35865C]">Incoming player</div>
      <Pick list={bench} val={inn} set={setInn} kind="in" />
      <Button data-testid="request-sub-btn" className="mt-3 h-12 w-full rounded-[14px]" disabled={!out || !inn} onClick={async () => {
        try { await api.post(`/matches/${m.id}/substitution-requests`, { team_id: teamId, player_out_id: out, player_in_id: inn }); toast.success("Request sent to fourth official"); setOut(""); setInn(""); load(); } catch (e) { toast.error(errMsg(e)); }
      }}>Send to fourth official</Button>
      <div className="mt-3 space-y-1">{list.map((r) => <div key={r.id} className="flex justify-between rounded-[12px] bg-[#F7F8FA] px-3 py-2 text-xs" data-testid={`sub-request-${r.status}`}><span>#{P(r.player_out_id)?.number} {P(r.player_out_id)?.short_name} → #{P(r.player_in_id)?.number} {P(r.player_in_id)?.short_name}</span><b>{label(r.status)}</b></div>)}</div>
    </Panel>
  );
}

function Board({ team, matchId, room }) {
  const m = room?.match;
  const [players, setPlayers] = useState([]);
  const [formation, setFormation] = useState("4-3-3");
  const [starting, setStarting] = useState([]);
  const [subs, setSubs] = useState([]);
  const [positions, setPositions] = useState({});
  const [notes, setNotes] = useState("");
  const [presets, setPresets] = useState([]);
  const [pname, setPname] = useState("");
  const [init, setInit] = useState(false);
  const live = m && !PRE.includes(m.status);
  const lu = m?.lineups?.find((l) => l.team_id === team.id);
  useEffect(() => { api.get("/players", { params: { team_id: team.id } }).then((r) => setPlayers(r.data)); }, [team.id]);
  useEffect(() => {
    if (init || (matchId && !m)) return;
    api.get(`/tactics/${team.id}`, { params: { match_id: matchId || undefined } }).then((r) => {
      setPresets(r.data.presets);
      const cur = r.data.current;
      if (cur) { setFormation(cur.formation); setPositions(cur.positions || {}); setNotes(cur.notes || ""); }
      if (lu) { setStarting(lu.starting); setSubs(lu.subs); if (!cur) setFormation(lu.formation); }
      setInit(true);
    });
  }, [team.id, matchId, m, lu, init]);
  const byId = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const onFieldIds = live ? lu?.on_field || [] : starting;
  const pitchPlayers = onFieldIds.map((id) => byId[id] || m?.players?.[id]).filter(Boolean);
  const slots = formationSlots(formation);
  const pos = live ? { ...layoutPositions(lu, room.events, { positions, formation }) } : Object.fromEntries(starting.map((id, i) => [id, positions[id] || slots[i] || { x: 50, y: 50 }]));
  const toggle = (id, kind) => {
    if (live || lu?.status === "approved") return;
    const s = starting.filter((x) => x !== id), b = subs.filter((x) => x !== id);
    if (kind === "xi" && !starting.includes(id)) { if (s.length >= 11) return toast.error("Starting XI is full"); s.push(id); }
    if (kind === "sub" && !subs.includes(id)) b.push(id);
    setStarting(s); setSubs(b);
  };
  const changeFormation = (f) => { setFormation(f); if (f !== "Custom") setPositions({}); };
  const save = async (quiet) => {
    try { await api.put(`/tactics/${team.id}`, { formation, positions: pos, notes, match_id: matchId || null }); if (!quiet) toast.success(live ? "Tactical positions updated — official lineup unchanged" : "Tactics saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const sheet = async (submit) => {
    try { const { data } = await api.put(`/matches/${matchId}/lineup/${team.id}`, { starting, subs, formation, submit }); toast.success(submit ? "Official team sheet submitted" : "Draft saved"); await save(true); room.load(); return data; } catch (e) { toast.error(errMsg(e)); }
  };
  const P = live ? playersOf(m, team.id) : null;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="min-w-0 tro-card p-3 md:p-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-1" data-testid="formation-selector">
            {FORMATIONS.map((f) => <button key={f} data-testid={`formation-${f}`} onClick={() => changeFormation(f)} className={`min-h-[40px] shrink-0 rounded-[12px] px-3 text-xs font-extrabold transition-colors ${formation === f ? "bg-[#171B20] text-white" : "border border-[#E7EAEE] bg-[#F7F8FA] hover:bg-[#E7EAEE]"}`}>{f}</button>)}
          </div>
          <Button className="h-10 shrink-0 rounded-[12px]" data-testid="save-tactics-btn" onClick={() => save()}>Save</Button>
        </div>
        <div className="mb-2 text-xs text-[#6C757D]">Drag player photographs to reposition. {live ? "Live: tactical changes never alter the official lineup or create substitutions." : ""}</div>
        <Pitch players={pitchPlayers} positions={pos} state={m?.player_state || {}} draggable testid="coach-pitch"
          onMove={(id, p) => { setPositions((x) => ({ ...pos, ...x, [id]: p })); if (formation !== "Custom") setFormation((f) => f); }} />
        <Textarea data-testid="tactical-notes" className="mt-3" placeholder="Tactical notes (private to coaching staff)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="min-w-0 space-y-4">
        {matchId && !live && (
          <Panel title="Team sheet" testid="team-sheet-panel" action={<StatusBadge status={lu?.status || "draft"} testid="team-sheet-status" />}>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold" data-testid="lineup-counts"><span className={`rounded-full px-2 py-0.5 ${starting.length === 11 ? "bg-[#35865C] text-white" : "bg-[#FFD400]"}`}>Starting XI {starting.length}/11</span><span className="rounded-full bg-[#E7EAEE] px-2 py-0.5">Substitutes {subs.length}</span></div>
            <div className="max-h-[420px] space-y-1 overflow-auto">{players.map((p) => (
              <div key={p.id} className="flex min-h-[52px] items-center gap-2 rounded-[14px] border border-[#E7EAEE] p-1.5 text-sm">
                <Avatar player={p} size={36} /><span className="min-w-0 flex-1 truncate font-semibold">#{p.number} {p.short_name} <span className="text-[10px] text-[#6C757D]">{p.position}{p.suspended ? " · SUSPENDED" : ""}</span></span>
                <button data-testid={`xi-${p.number}`} onClick={() => toggle(p.id, starting.includes(p.id) ? "none" : "xi")} className={`h-10 w-11 rounded-[12px] text-[11px] font-extrabold ${starting.includes(p.id) ? "bg-[#171B20] text-white" : "bg-[#F7F8FA] border border-[#E7EAEE]"}`}>XI</button>
                <button data-testid={`sub-${p.number}`} onClick={() => toggle(p.id, subs.includes(p.id) ? "none" : "sub")} className={`h-10 w-11 rounded-[12px] text-[11px] font-extrabold ${subs.includes(p.id) ? "bg-[#343A40] text-white" : "bg-[#F7F8FA] border border-[#E7EAEE]"}`}>SUB</button>
              </div>))}</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" className="h-12 rounded-[14px]" data-testid="save-draft-btn" onClick={() => sheet(false)}>Save draft</Button>
              <Button className="h-12 rounded-[14px]" data-testid="submit-team-sheet-btn" disabled={starting.length !== 11 || lu?.status === "approved"} onClick={() => sheet(true)}>Submit team sheet</Button>
            </div>
          </Panel>
        )}
        {live && <SubRequests m={m} teamId={team.id} onField={P.onField} bench={P.bench} />}
        {live && <TeamCards m={m} players={[...P.onField, ...P.bench, ...(P.all || [])]} />}
        {live && <Panel title="Official match events"><EventTimeline compact events={room.events} match={m} /></Panel>}
        <Panel title="Tactical presets" testid="presets-panel">
          <div className="flex gap-2"><Input data-testid="preset-name-input" placeholder="Preset name" value={pname} onChange={(e) => setPname(e.target.value)} />
            <Button variant="outline" data-testid="save-preset-btn" onClick={async () => { const { data } = await api.post(`/tactics/${team.id}/presets`, { formation, positions: pos, name: pname || formation, notes }); setPresets([...presets, data]); setPname(""); toast.success("Preset saved"); }}>Save</Button></div>
          <div className="mt-2 flex flex-wrap gap-1.5">{presets.map((p) => <button key={p.id} data-testid={`preset-${p.name}`} onClick={() => { setFormation(p.formation); setPositions(p.positions); }} className="rounded-full bg-[#F7F8F9] px-3 py-1 text-xs font-bold hover:bg-[#E6E9ED]">{p.name}</button>)}</div>
        </Panel>
      </div>
    </div>
  );
}

function LiveBoard({ team, matchId }) {
  const room = useMatch(matchId, { onMessage: (msg) => { if (msg.type === "event" && ["yellow", "second_yellow", "red"].includes(msg.event?.type)) toast.warning(`${label(msg.event.type)} · #${msg.player?.number} ${msg.player?.name}`); } });
  if (!room.match) return <Spinner />;
  const pre = PRE.includes(room.match.status);
  return <div className="space-y-4"><div className={pre ? "" : "sticky top-16 z-30"}><TacticalHeader match={room.match} elapsed={room.elapsed} conn={room.conn} /></div><Board team={team} matchId={matchId} room={room} /></div>;
}

function TeamCards({ m, players }) {
  const seen = new Set();
  const list = players.filter((p) => p && !seen.has(p.id) && seen.add(p.id) && (m.player_state?.[p.id]?.yellow || m.player_state?.[p.id]?.red));
  return (
    <Panel title="Official cards · my team" testid="coach-team-cards">
      <p className="mb-2 text-[11px] text-[#6C757D]">Issued by the referee. Coaches cannot issue or edit disciplinary events.</p>
      {!list.length && <div className="text-sm text-[#6C757D]">No cards for your team.</div>}
      <div className="space-y-1.5">{list.map((p) => {
        const st = m.player_state[p.id];
        return (
          <div key={p.id} className="flex items-center gap-2 rounded-[12px] bg-[#F7F8FA] p-1.5 text-sm" data-testid={`coach-card-${p.number}`}>
            <Avatar player={p} size={30} /><b className="min-w-0 flex-1 truncate">#{p.number} {p.short_name}</b>
            {st.yellow > 0 && <span className="h-5 w-3.5 rounded-[3px] bg-[#FFD400]" />}{st.yellow > 1 && <span className="h-5 w-3.5 rounded-[3px] bg-[#FFD400]" />}{st.red && <span className="h-5 w-3.5 rounded-[3px] bg-[#F02030]" />}
            <span className="text-[10px] font-bold uppercase text-[#6C757D]">{st.red ? "Dismissed" : "Cautioned"}</span>
          </div>
        );
      })}</div>
    </Panel>
  );
}

export default function TacticsBoard() {
  const teams = useCoachTeams();
  const [sp, setSp] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const team = teams.find((t) => t.id === sp.get("team")) || teams[0];
  useEffect(() => {
    if (team) api.get("/matches", { params: { scope: "mine", team_id: team.id } }).then((r) => setMatches(r.data.filter((m) => !["validated", "archived", "cancelled"].includes(m.status))));
  }, [team]);
  if (!teams.length) return <Empty text="No coaching assignment found." />;
  if (!team) return <Spinner />;
  const mid = sp.get("match") ?? (matches.find((m) => !PRE.includes(m.status)) || matches[0])?.id ?? "";
  return (
    <div className="space-y-4" data-testid="coach-tactical-board">
      <PageHeader title="Tactical Board" sub={`${team.name} · drag, position and prepare`}>
        <select data-testid="board-match-select" value={mid} onChange={(e) => setSp({ team: team.id, match: e.target.value })} className="h-10 w-full min-w-0 max-w-full rounded-xl border px-3 text-sm font-semibold sm:w-auto sm:max-w-md">
          <option value="">No fixture (training board)</option>
          {matches.map((m) => <option key={m.id} value={m.id}>{m.match_number} · {m.home.name} v {m.away.name} · {label(m.status)}</option>)}
        </select>
      </PageHeader>
      {mid ? <LiveBoard key={mid} team={team} matchId={mid} /> : <Board key="free" team={team} matchId={null} />}
    </div>
  );
}
